import { Fragment, useCallback, useMemo, useState } from "react";
import { useGlobalSearchParams } from "expo-router";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  CheckBoxWithLabel,
  Chips,
  GeneralDialog,
  InputWithLabel,
  MatchCase,
  XInCircle,
} from "@ptsd/uikit";
import type { ControllerRenderProps } from "react-hook-form";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { CheckedState } from "tamagui";
import {
  Dialog,
  Form,
  ScrollView,
  Text,
  Button as TGButton,
  View,
  XStack,
  YStack,
} from "tamagui";
import { z } from "zod";

import { api } from "~/utils/api";
import { RowVirtualizer } from "~/components/blocks";
import type { Slices } from "./types";

enum Step {
  groups = "groups",
  users = "users",
}

const ParticipantFormSchema = z.object({
  step: z.nativeEnum(Step).default(Step.groups),
  groupIds: z.array(z.string()).optional(),
  users: z
    .array(
      z.object({
        id: z.string(),
        email: z.string().email(),
        groupId: z.string(),
        groupName: z.string().optional(),
      }),
    )
    .min(1, "Оберіть принаймні одного учасника, щоб продовжити."),
});

type ParticipantFormSchemaType = z.infer<typeof ParticipantFormSchema>;

export const AddParticipantsDialog = () => {
  const [open, setOpen] = useState(false);
  const { researchId } = useGlobalSearchParams<{
    researchId: string;
  }>();

  const form = useForm<ParticipantFormSchemaType>({
    resolver: zodResolver(ParticipantFormSchema),
    defaultValues: {
      users: [],
      step: Step.groups,
      groupIds: [],
    },
  });

  const apiUtils = api.useUtils();

  const [isFirstSliceFinnished] =
    api.research.reseachHasFinnishedFirstSlice.useSuspenseQuery({
      researchId,
    });

  const { mutate } = api.research.addParticipants.useMutation({
    //TODO: show success message
    onSuccess: () => {
      void apiUtils.research.participants.invalidate();
      setOpen(false);
    },
  });

  const onSubmit = (data: ParticipantFormSchemaType) => {
    mutate({
      researchId,
      ids: data.users.map((user) => user.id),
    });
    form.reset();
  };

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) form.reset();
    },
    [form],
  );

  const currentStep = form.watch("step");

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild disabled={isFirstSliceFinnished}>
        <Button
          size="md"
          rounded="lg"
          variant="primary"
          disabled={isFirstSliceFinnished}
        >
          + Запросити учасника
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <GeneralDialog.Overlay />

        <Dialog.Content
          maxHeight={700}
          minWidth={1000}
          backgroundColor={"white"}
          borderRadius={"$5"}
          padding={"$5"}
        >
          <FormProvider {...form}>
            <AddParticipantsHeader />
            <Form onSubmit={form.handleSubmit(onSubmit)}>
              <MatchCase value={currentStep}>
                {(Case) => (
                  <>
                    <Case condition={Step.groups}>
                      <AddParticipantsGroups />
                    </Case>
                    <Case condition={Step.users}>
                      <AddParticipantsUsers />
                    </Case>
                  </>
                )}
              </MatchCase>
            </Form>
          </FormProvider>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

const AddParticipantsGroups = () => {
  const form = useFormContext<ParticipantFormSchemaType>();
  const handleNextStep = useCallback(() => {
    form.setValue("step", Step.users);
  }, [form]);

  return (
    <YStack gap={"$4"}>
      <SelectGroups />
      <XStack gap={"$2"}>
        <Dialog.Close asChild>
          <Button rounded="lg" variant="outlined" size="md" minWidth={140}>
            Скасувати
          </Button>
        </Dialog.Close>
        <Button
          rounded="lg"
          variant="primary"
          size="md"
          minWidth={220}
          onPress={handleNextStep}
        >
          Далі
        </Button>
      </XStack>
    </YStack>
  );
};

const AddParticipantsUsers = () => {
  const { t } = useTranslation("research");
  const form = useFormContext<ParticipantFormSchemaType>();
  const handleBackStep = useCallback(() => {
    form.setValue("step", Step.groups);
  }, [form]);

  return (
    <Fragment>
      <SelectUsers />
      <SelectedUsers />
      <XStack gap={"$2"}>
        <Button
          rounded="lg"
          variant="outlined"
          size="md"
          minWidth={140}
          onPress={handleBackStep}
        >
          {t("add-participants.actions.back")}
        </Button>
        <Form.Trigger asChild>
          <Button rounded="lg" variant="primary" size="md" minWidth={220}>
            {t("add-participants.actions.confirm")}
          </Button>
        </Form.Trigger>
      </XStack>
    </Fragment>
  );
};

const SelectGroups = () => {
  const form = useFormContext<ParticipantFormSchemaType>();

  const selectedUsers = form.watch("users");

  const [groups] = api.group.getGroupNamesWithId.useSuspenseQuery();

  const { type } = useGlobalSearchParams<{
    type: Slices;
  }>();

  const [users] = api.research.getAvailableUsers.useSuspenseQuery(
    { type },
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
    },
  );

  const changeGroupIds = useCallback(
    (
      field: ControllerRenderProps<ParticipantFormSchemaType, "groupIds">,
      groupId: string,
      checked: CheckedState,
    ) => {
      const { value = [] } = field;

      const nextGroupIds = checked
        ? [...value, groupId]
        : value.filter((id) => id !== groupId);

      field.onChange(nextGroupIds);

      const incomingUsers = checked
        ? users.filter(
            (u) => groupId === u.groupId && u.activeResearchName === undefined,
          )
        : [];

      const selectedGroupUsers = users.filter((u) => groupId === u.groupId);

      form.setValue(
        "users",
        Array.from(
          new Map(
            [
              ...selectedUsers.filter(
                ({ id }) => !selectedGroupUsers.some((user) => user.id === id),
              ),
              ...(checked ? incomingUsers : []),
            ].map((item) => [item.id, item]),
          ).values(),
        ),
      );
    },
    [users, selectedUsers, form],
  );

  return (
    <View>
      <RowVirtualizer
        count={groups.length}
        estimateSize={48}
        height={445}
        maxWidth={440}
        style={{ direction: "rtl" }}
        contentContainerStyle={{ direction: "ltr", paddingLeft: "$2.3" }}
      >
        {(virtualItem) => {
          const group = groups[virtualItem.index];
          if (group === undefined) return null;

          return (
            <View
              key={virtualItem.key}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <Controller
                name="groupIds"
                control={form.control}
                render={({ field }) => (
                  <View height="$5.3" justifyContent="flex-end">
                    <View
                      height="$3.4"
                      borderBottomWidth={"$0.2"}
                      borderBottomColor={"$gray-100"}
                    >
                      <CheckBoxWithLabel
                        checked={!!field.value?.includes(group.id)}
                        onCheckedChange={(checked) =>
                          changeGroupIds(field, group.id, checked)
                        }
                        renderLabel={
                          <Text
                            fontFamily="$body"
                            color="$gray-950"
                            fontWeight="$5"
                            fontSize="$2"
                            lineHeight="$2"
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            {group.name}
                          </Text>
                        }
                      />
                    </View>
                  </View>
                )}
              />
            </View>
          );
        }}
      </RowVirtualizer>
    </View>
  );
};

const AddParticipantsHeader = () => {
  const { t } = useTranslation("research");
  const form = useFormContext<ParticipantFormSchemaType>();
  const type = form.getValues("step");

  return (
    <YStack gap={"$2"} marginBottom={"$4"}>
      <Dialog.Title
        fontFamily={"$subtitle"}
        fontWeight={"$6"}
        fontSize={"$6"}
        lineHeight={"$6"}
        color={"$gray-950"}
      >
        {t(`add-participants.${type}.title`)}
      </Dialog.Title>
      <Dialog.Description
        fontFamily={"$body"}
        fontWeight={"$4"}
        fontSize={"$3"}
        lineHeight={"$3"}
        color={"$gray-800"}
      >
        {t(`add-participants.${type}.description`)}
      </Dialog.Description>
    </YStack>
  );
};

const SelectedUsers = () => {
  const form = useFormContext<z.infer<typeof ParticipantFormSchema>>();
  const users = form.watch("users");

  return (
    <View marginTop={"$1.3"} marginBottom={"$3"}>
      <ScrollView
        borderBottomColor={"$gray-100"}
        borderTopColor={"$gray-100"}
        borderTopWidth={"$0.2"}
        borderBottomWidth={"$0.2"}
        paddingVertical={"$2"}
        maxWidth={902}
        height={108}
      >
        <View flexWrap="wrap" flexDirection="row" gap={"$1"}>
          {users?.map((user) => (
            <Chips.Chip
              key={user.id}
              selected={false}
              backgroundColor="#0B564C"
              gap={"$0.5"}
            >
              {(ChipText, conf) => (
                <>
                  <ChipText color={conf.backgroundColor}>{user.email}</ChipText>
                  <TGButton
                    cursor="pointer"
                    onPress={() =>
                      form.setValue(
                        "users",
                        users?.filter((u) => u.id !== user.id),
                      )
                    }
                    padding={0}
                    unstyled
                  >
                    <XInCircle />
                  </TGButton>
                </>
              )}
            </Chips.Chip>
          ))}
        </View>
      </ScrollView>
    </View>
  );
};

const SelectUsers = () => {
  const [email, setEmail] = useState<string>("");
  const { type } = useGlobalSearchParams<{
    type: Slices;
  }>();

  const form = useFormContext<z.infer<typeof ParticipantFormSchema>>();

  const { t } = useTranslation("research");

  const [data] = api.research.getAvailableUsers.useSuspenseQuery({
    type,
  });

  const filteredUsers = useMemo(
    () =>
      data.filter(
        ({ email: participantEmail, activeResearchName }) =>
          participantEmail.toLowerCase().includes(email.toLowerCase().trim()) &&
          activeResearchName === undefined,
      ),

    [data, email],
  );

  const activeResearchName = useMemo(
    () =>
      data.find((participant) => participant.email === email)
        ?.activeResearchName,
    [data, email],
  );

  return (
    <View paddingTop={"$2"}>
      <InputWithLabel
        isError={activeResearchName !== undefined}
        error={"Користувач вже доданий в дослідження " + activeResearchName}
        label={t(
          "create-research-funnel.third-step-research-users.input-label",
        )}
        variant="inline"
        placeholder={t(
          "create-research-funnel.third-step-research-users.input-placeholder",
        )}
        maxWidth={440}
        onChangeText={setEmail}
      />
      <RowVirtualizer
        count={filteredUsers.length}
        estimateSize={48}
        height={265}
        maxWidth={440}
        style={{ direction: "rtl" }}
        contentContainerStyle={{ direction: "ltr", paddingLeft: "$2.3" }}
      >
        {(virtualItem) => {
          const user = filteredUsers[virtualItem.index];
          if (user === undefined) return null;
          return (
            <View
              key={virtualItem.key}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              <Controller
                name="users"
                control={form.control}
                render={({ field }) => {
                  const { ref: _, onChange, value } = field;
                  return (
                    <View height="$5.3" justifyContent="flex-end">
                      <View
                        height="$3.4"
                        borderBottomWidth={"$0.2"}
                        borderBottomColor={"$gray-100"}
                      >
                        <CheckBoxWithLabel
                          checked={value?.map((u) => u.id).includes(user?.id)}
                          renderLabel={
                            <Text
                              fontFamily="$body"
                              color="$gray-950"
                              fontWeight="$5"
                              fontSize="$2"
                              lineHeight="$2"
                              numberOfLines={1}
                              ellipsizeMode="tail"
                            >
                              {user?.email}
                            </Text>
                          }
                          onCheckedChange={(checked) => {
                            if (value === undefined) return;
                            return checked
                              ? onChange([...value, user])
                              : onChange(
                                  value?.filter(
                                    (value) => value.id !== user?.id,
                                  ),
                                );
                          }}
                        />
                      </View>
                    </View>
                  );
                }}
              />
            </View>
          );
        }}
      </RowVirtualizer>
      {form.formState.errors.users?.message && (
        <Text
          paddingLeft={"$2.5"}
          paddingTop={"$2"}
          color={"$error-500"}
          fontFamily={"$body"}
          fontSize={"$2"}
        >
          {form.formState.errors.users.message}
        </Text>
      )}
    </View>
  );
};
